import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  alpha,
  TableFooter,
  Divider
} from '@mui/material';
import {
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  SwapHoriz as SwapHorizIcon
} from '@mui/icons-material';
import relatoriosApi from '../../services/relatoriosApi';

const RelatorioServicosExecutados = ({ 
  customTheme, 
  carregando = false, 
  filtros = {},
  erro = null,
  forceUpdate = 0 // Prop para forçar atualização
}) => {
  const [dadosServicos, setDadosServicos] = useState([]);
  const [carregandoInterno, setCarregandoInterno] = useState(false);
  const [erroInterno, setErroInterno] = useState(null);

  // Função para buscar dados dos serviços executados
  const buscarDadosServicosExecutados = async () => {
    try {
      setCarregandoInterno(true);
      setErroInterno(null);
      
      console.log('[DEBUG] RelatorioServicosExecutados - Buscando dados com filtros:', filtros);
      
      const filtrosApi = {
        dataInicio: filtros.dataInicio,
        dataFim: filtros.dataFim
      };
      
      const response = await relatoriosApi.buscarDadosServicosExecutados(filtrosApi);
      
      if (response.success) {
        setDadosServicos(response.data || []);
        console.log('[DEBUG] RelatorioServicosExecutados - Dados recebidos:', response.data);
      } else {
        setErroInterno(response.message || 'Erro ao buscar dados dos serviços executados');
        setDadosServicos([]);
      }
    } catch (error) {
      console.error('[DEBUG] RelatorioServicosExecutados - Erro:', error);
      setErroInterno('Erro ao carregar dados dos serviços executados');
      setDadosServicos([]);
    } finally {
      setCarregandoInterno(false);
    }
  };

  // Carregar dados quando os filtros mudarem ou quando forceUpdate mudar
  useEffect(() => {
    if (filtros.dataInicio && filtros.dataFim) {
      buscarDadosServicosExecutados();
    }
  }, [filtros.dataInicio, filtros.dataFim, forceUpdate]);

  // Calcular totais
  const totais = dadosServicos.reduce(
    (acc, servico) => ({
      servicosCadastrados: acc.servicosCadastrados + 1,
      senhasFinalizadas: acc.senhasFinalizadas + servico.senhas_finalizadas,
      alteracoesServico: acc.alteracoesServico + servico.alteracoes_servico
    }),
    { servicosCadastrados: 0, senhasFinalizadas: 0, alteracoesServico: 0 }
  );

  const estaCarregando = carregando || carregandoInterno;
  const erroExibir = erro || erroInterno;

  if (estaCarregando) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (erroExibir) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {erroExibir}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        Relatório de Serviços Executados
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Análise detalhada dos serviços cadastrados, quantidade de senhas finalizadas e alterações realizadas.
      </Typography>

      <TableContainer 
        component={Paper} 
        elevation={0}
        sx={{
          border: `1px solid ${alpha(customTheme.palette.primary.main, 0.2)}`,
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: alpha(customTheme.palette.primary.main, 0.1) }}>
              <TableCell sx={{ fontWeight: 600, color: customTheme.palette.primary.main }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon fontSize="small" />
                  Serviço
                </Box>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: customTheme.palette.primary.main }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <CheckCircleIcon fontSize="small" />
                  Senhas Finalizadas
                </Box>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: customTheme.palette.primary.main }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <SwapHorizIcon fontSize="small" />
                  Alterações de Serviço
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          
          <TableBody>
            {dadosServicos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    Nenhum dado encontrado para o período selecionado
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              dadosServicos.map((servico, index) => (
                <TableRow 
                  key={servico.nome || index}
                  sx={{
                    '&:hover': {
                      backgroundColor: alpha(customTheme.palette.primary.main, 0.05)
                    }
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body1" fontWeight={500}>
                        {servico.nome}
                      </Typography>
                      {servico.ativo && (
                        <Chip 
                          label="Ativo" 
                          size="small" 
                          color="success" 
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </TableCell>
                  
                  <TableCell align="center">
                    <Chip
                      label={servico.senhas_finalizadas}
                      color="primary"
                      variant="outlined"
                      sx={{
                        fontWeight: 600,
                        minWidth: 60
                      }}
                    />
                  </TableCell>
                  
                  <TableCell align="center">
                    <Chip
                      label={servico.alteracoes_servico}
                      color={servico.alteracoes_servico > 0 ? "warning" : "default"}
                      variant="outlined"
                      sx={{
                        fontWeight: 600,
                        minWidth: 60
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
          
          {dadosServicos.length > 0 && (
            <TableFooter>
              <TableRow sx={{ backgroundColor: alpha(customTheme.palette.primary.main, 0.05) }}>
                <TableCell sx={{ fontWeight: 700, borderTop: `2px solid ${customTheme.palette.primary.main}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" fontWeight={700}>
                      TOTAIS ({totais.servicosCadastrados} serviços)
                    </Typography>
                  </Box>
                </TableCell>
                
                <TableCell 
                  align="center" 
                  sx={{ 
                    fontWeight: 700, 
                    borderTop: `2px solid ${customTheme.palette.primary.main}`,
                    color: customTheme.palette.primary.main
                  }}
                >
                  <Typography variant="h6" fontWeight={700}>
                    {totais.senhasFinalizadas}
                  </Typography>
                </TableCell>
                
                <TableCell 
                  align="center" 
                  sx={{ 
                    fontWeight: 700, 
                    borderTop: `2px solid ${customTheme.palette.primary.main}`,
                    color: totais.alteracoesServico > 0 ? customTheme.palette.warning.main : customTheme.palette.text.secondary
                  }}
                >
                  <Typography variant="h6" fontWeight={700}>
                    {totais.alteracoesServico}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>

      {dadosServicos.length > 0 && (
        <Box sx={{ mt: 3, p: 2, bgcolor: alpha(customTheme.palette.info.main, 0.1), borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Legenda:</strong> Este relatório mostra todos os serviços cadastrados no sistema, 
            a quantidade de senhas finalizadas para cada serviço no período selecionado, 
            e o número de vezes que o serviço foi alterado durante o atendimento.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default RelatorioServicosExecutados; 